// src/services/retargeting/gtm.service.ts

/**
 * Google Ads Conversion Tracking Service
 */

export enum GTMConversionType {
    PAGE_VIEW = 'AW-17603418624/xP6xCMfP46cbEIC0-8lB',
    FIRST_MESSAGE = 'AW-17603418624/LNNDCNLA7acbEIC0-8lB',
}

export interface GTMConversionParams {
    value?: number;
    currency?: string;
    transaction_id?: string;
    event_category?: string;
    event_label?: string;
    [key: string]: any;
}

declare global {
    interface Window {
        gtag: {
            (command: 'js', date: Date): void;
            (command: 'config', targetId: string, config?: any): void;
            (command: 'event', eventName: string, eventParams?: any): void;
            (...args: any[]): void;
        };
        dataLayer: any[];
    }
}

class GTMService {
    private static instance: GTMService;
    private googleAdsId = 'AW-17603418624';
    private isInitialized = false;
    private scriptLoaded = false;

    private constructor() {}

    static getInstance(): GTMService {
        if (!GTMService.instance) {
            GTMService.instance = new GTMService();
        }
        return GTMService.instance;
    }

    initialize(): void {
        if (this.isInitialized) return;
        if (typeof window === 'undefined') return;

        this.loadGoogleAds();
        this.isInitialized = true;
    }

    private loadGoogleAds(): void {
        if (this.scriptLoaded) return;

        window.dataLayer = window.dataLayer || [];

        window.gtag = function() {
            window.dataLayer.push(arguments);
        };

        window.gtag('js', new Date());
        window.gtag('config', this.googleAdsId, {
            'allow_ad_personalization_signals': true,
            'send_page_view': true
        });

        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.googleAdsId}`;

        script.onload = () => {
            this.scriptLoaded = true;
        };

        script.onerror = () => {
            console.error('Failed to load Google Ads script');
        };

        document.head.appendChild(script);
    }

    trackConversion(
        conversionType: GTMConversionType,
        params: GTMConversionParams = {}
    ): void {
        if (typeof window === 'undefined' || !window.gtag) {
            console.warn('Google Ads not initialized');
            return;
        }

        try {
            const defaultParams: GTMConversionParams = {
                value: 1.0,
                currency: 'CAD',
                ...params,
            };

            window.gtag('event', 'conversion', {
                send_to: conversionType,
                ...defaultParams,
            });

        } catch (error) {
            console.error('Failed to track Google Ads conversion:', error);
        }
    }

    trackPageView(): void {
        this.trackConversion(GTMConversionType.PAGE_VIEW);
    }

    trackFirstMessage(assistantName?: string): void {
        const transactionId = `first_msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        this.trackConversion(GTMConversionType.FIRST_MESSAGE, {
            transaction_id: transactionId,
            event_label: assistantName,
        });
    }

    isReady(): boolean {
        return this.scriptLoaded;
    }
}

export const gtmService = GTMService.getInstance();
